import { useCreateOrganisation } from '@/hooks/useOrganisations';
import { AlertCircle, Building2, KeyRound, X } from 'lucide-react';
import { useState } from 'react';

interface CreateOrganisationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateOrganisationModal({
  isOpen,
  onClose,
}: CreateOrganisationModalProps) {
  const [name, setName] = useState('');
  const [openPayeUsername, setOpenPayeUsername] = useState('');
  const [openPayePassword, setOpenPayePassword] = useState('');
  const createOrg = useCreateOrganisation();

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !openPayeUsername.trim() || !openPayePassword.trim())
      return;
    createOrg.mutate(
      {
        name: name.trim(),
        openPayeUsername: openPayeUsername.trim(),
        openPayePassword,
      },
      {
        onSuccess: () => {
          setName('');
          setOpenPayeUsername('');
          setOpenPayePassword('');
          onClose();
        },
      }
    );
  };

  const isFormValid =
    name.trim() && openPayeUsername.trim() && openPayePassword.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl border border-neutral-200 bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">
            Créer une organisation
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-neutral-400 transition-colors hover:text-neutral-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Organisation */}
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-500">
              <Building2 className="h-4 w-4" />
              Organisation
            </div>
            <div>
              <label
                htmlFor="org-name"
                className="mb-1 block text-sm font-medium text-neutral-700"
              >
                Nom de l'organisation
              </label>
              <input
                id="org-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Mon cabinet comptable"
                className="focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-1 focus:outline-none"
                autoFocus
              />
            </div>
          </div>

          {/* OpenPaye */}
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-medium text-neutral-500">
              <KeyRound className="h-4 w-4" />
              Identifiants OpenPaye
            </div>
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="openpaye-username"
                  className="mb-1 block text-sm font-medium text-neutral-700"
                >
                  Identifiant OpenPaye (email)
                </label>
                <input
                  id="openpaye-username"
                  type="email"
                  value={openPayeUsername}
                  onChange={e => setOpenPayeUsername(e.target.value)}
                  placeholder="votre@email.com"
                  className="focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-1 focus:outline-none"
                />
              </div>
              <div>
                <label
                  htmlFor="openpaye-password"
                  className="mb-1 block text-sm font-medium text-neutral-700"
                >
                  Mot de passe OpenPaye
                </label>
                <input
                  id="openpaye-password"
                  type="password"
                  value={openPayePassword}
                  onChange={e => setOpenPayePassword(e.target.value)}
                  placeholder="••••••••"
                  className="focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:ring-1 focus:outline-none"
                />
              </div>
            </div>
            <p className="mt-2 text-xs text-neutral-500">
              Vos identifiants seront vérifiés par connexion à OpenPaye, puis
              stockés de manière chiffrée.
            </p>
          </div>

          {/* Error */}
          {createOrg.isError && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {(createOrg.error as HttpApiError)?.response?.data?.message ||
                  "Erreur lors de la création de l'organisation"}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!isFormValid || createOrg.isPending}
              className="bg-brand-600 hover:bg-brand-700 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors disabled:opacity-50"
            >
              {createOrg.isPending
                ? 'Vérification et création...'
                : "Créer l'organisation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
